<script lang="ts">
  import { ForumTagName, ForumType, type ForumDataList } from '$shared/lib';
  import Button from '../../Button.svelte';
  import TextInput from '../../TextInput.svelte';
  import DropdownInput from '../../dropdowns/DropdownInput.svelte';
  import LockIcon from '$lib/icons/Lock.svelte';
  import PostEditorTextArea from './PostEditorTextArea.svelte';
  import PlusIcon from '$icons/PlusIcon.svelte';
  import { cn } from '$lib/utils/misc';
  import PostEditorResizeManager from './PostEditorResizeManager.svelte';
  import PostEditorHeader from './PostEditorHeader.svelte';
  import { getPostEditor } from '$lib/stores/postEditorStore';
  import SaveIcon from '$icons/SaveIcon.svelte';
  import ClosePostEditorModal from './ClosePostEditorModal.svelte';
  import ForumComboboxInput from './ForumComboboxInput.svelte';
  import { browser } from '$app/environment';

  export let forums: ForumDataList;

  let formElement: HTMLFormElement;
  let editorElement: HTMLDivElement | undefined = undefined;
  let editorContainer: HTMLDivElement;
  let isResizing = false;
  let showCloseModal = false;
  let isForumTagsDisabled = false;
  let showHotkeyCheatsheet = false;

  const {
    editor,
    superform: { form, errors, constraints, enhance, submitting, tainted, isTainted },
    isInitialized,
    openState,
    editorHeight,
    activePost,
    close,
  } = getPostEditor();

  // This is used to prevent the editor from animating to its initial position
  // when the page is first loaded. This is because the editor is positioned
  // absolutely, and the browser will not apply the styles until the next frame.
  let shouldApplyTransitions = false;
  $: if (browser && isInitialized) {
    requestAnimationFrame(() => {
      shouldApplyTransitions = true;
    });
  }

  function closeEditor() {
    if (isTainted($tainted)) {
      showCloseModal = true;
    } else {
      close();
    }
  }

  // Clear tags if the forum type is not public
  $: if ($form.forumType !== ForumType.PUBLIC) {
    isForumTagsDisabled = true;
    form.update((form) => {
      form.tags = [];
      return form;
    });
  } else {
    isForumTagsDisabled = false;
  }
</script>

{#if isInitialized}
  <div
    bind:this={editorContainer}
    class={cn(
      'bg-gray-dark-2 shadow-gray-dark-2 fixed bottom-0 left-[50%] z-50 w-[min(900px,100%)] -translate-x-1/2 shadow-lg',
      shouldApplyTransitions && 'transition-all duration-300 ease-in-out',
      $openState === 'fullscreen' && 'h-[100dvh] w-screen overflow-hidden rounded-none',
      $openState === 'normal' && 'w-[min(900px,100%)] min-w-[320px] rounded-t-lg',
      $openState === 'minimized' && 'w-[min(900px,100%)] min-w-[320px] rounded-t-lg',
      $openState === 'hidden' ? 'translate-y-full' : 'fixed',
      isResizing && 'transition-none',
    )}
    style={$openState !== 'fullscreen' ? `height: ${$editorHeight}px;` : ''}
  >
    <PostEditorResizeManager {closeEditor} bind:isResizing bind:showHotkeyCheatsheet />
    <div
      class={cn(
        'mx-auto flex h-full min-h-0 w-full max-w-[900px] flex-col overflow-hidden px-4 pb-6 pt-4',
      )}
    >
      <PostEditorHeader {closeEditor} />
      <form
        bind:this={formElement}
        method="POST"
        use:enhance
        class={cn(
          'gap-y-lg min-h-0 flex-1',
          $openState === 'minimized' ? 'hidden' : 'flex flex-col',
        )}
      >
        <input type="hidden" name="forumType" value={$form.forumType} />
        <TextInput
          label="Title"
          placeholder="Create a title for your post..."
          bind:value={$form.title}
          bind:errors={$errors.title}
          {...$constraints.title}
        />
        <div class="gap-x-lg flex flex-col sm:flex-row">
          <ForumComboboxInput
            bind:forumName={$form.forumName}
            bind:forumType={$form.forumType}
            errors={$errors.forumName}
            {forums}
            {...$constraints.forumName}
          />
          <DropdownInput
            optional
            disabled={$form.forumType !== ForumType.PUBLIC}
            multiple
            multipleLimit={3}
            label="Tags"
            placeholder="Add up to 3 tags..."
            class="mt-4 sm:mt-0"
            items={Object.values(ForumTagName).map((tag) => tag)}
            name="tags"
            bind:selectedValues={$form.tags}
            bind:errors={$errors.tags}
            {...$constraints.tags}
          />
        </div>
        <PostEditorTextArea
          {editor}
          errors={$errors.body?._errors}
          bind:element={editorElement}
          bind:showHotkeyCheatsheet
        />
        {#if $form.forumType === ForumType.ACCELERATOR}
          <div class="flex gap-2">
            <span class="text-orange-dark-10 flex items-center gap-2">
              <LockIcon />
              <p class="text-orange-dark-10 font-semibold">Accelerator forum</p>
            </span>
            <p>Viewable by accelerator founders and mentors only.</p>
          </div>
        {/if}
        <div
          class={cn(
            'flex w-full flex-row-reverse gap-x-4 sm:w-fit sm:flex-row sm:gap-x-2',
            $openState === 'fullscreen' && 'pb-8',
          )}
        >
          <Button
            type="submit"
            secondary
            size="sm"
            submitting={$submitting}
            class="flex-1 sm:flex-none"
          >
            {#if $activePost}
              <SaveIcon class="h-4 w-4" slot="left-icon" />
              Save changes
            {:else}
              <PlusIcon slot="left-icon" />
              Create post
            {/if}
          </Button>
          <Button tertiary size="sm" class="flex-1 sm:flex-none" on:click={closeEditor}>
            Close
          </Button>
        </div>
      </form>
    </div>
  </div>
  <ClosePostEditorModal bind:open={showCloseModal} />
{/if}
